/**
 * Troubleshooting Articles
 */

import type { HelpArticle } from "../types";

export const TROUBLESHOOTING_ARTICLES: HelpArticle[] = [
  {
    id: "ts-001",
    slug: "kyc-verification-issues",
    title: "KYC Verification Issues",
    description: "Resolving identity verification problems",
    category: "troubleshooting",
    popular: true,
    order: 1,
    keywords: ["kyc", "verification", "failed", "rejected", "problem"],
    content: `
# KYC Verification Issues

Having trouble with identity verification? Here's how to resolve common issues.

## Common Rejection Reasons

### Document Issues
| Problem | Solution |
|---------|----------|
| Blurry photo | Retake in good lighting |
| Glare on document | Avoid direct light |
| Expired ID | Use valid document |
| Corners cut off | Include all 4 corners |
| Wrong document type | Use passport/ID/license |

### Selfie Issues
| Problem | Solution |
|---------|----------|
| Face not visible | Remove glasses, face camera |
| Poor lighting | Use natural light |
| Photo doesn't match ID | Ensure same person |
| Eyes closed | Keep eyes open |
| Face covered | Remove masks/hats |

## Step-by-Step Retry

### 1. Check Requirements
- Valid, unexpired document
- Original (no photocopies)
- Clear, readable text
- Full document visible

### 2. Prepare Environment
- Good lighting (natural best)
- Stable camera/phone
- Clean camera lens
- Plain background

### 3. Take New Photos
- Hold steady, don't rush
- Fill frame with document
- No flash (causes glare)
- Review before submitting

## Still Failing?

### Manual Review
1. Go to Settings > Verification
2. Click "Request Manual Review"
3. Upload documents
4. Add explanation if needed
5. Wait 24-48 hours

### Contact Support
Email support@supabank.xyz with:
- Account email
- Issue description
- Screenshots of errors
- Photos you're trying to use

## Verification Status Meanings

| Status | Meaning |
|--------|---------|
| Pending | Being processed |
| Needs Info | Additional docs required |
| Rejected | See reason, retry |
| Approved | Verification complete |
    `.trim(),
  },
  {
    id: "ts-002",
    slug: "bank-account-connection-problems",
    title: "Bank Account Connection Problems",
    description: "Fixing issues with bank account setup",
    category: "troubleshooting",
    order: 2,
    keywords: ["bank", "account", "connection", "failed", "link"],
    content: `
# Bank Account Connection Problems

Trouble connecting your bank account? Here are solutions.

## Common Issues

### Incorrect Details
**Symptoms**: Account rejected immediately

**Solution**:
1. Double-check routing number (9 digits)
2. Verify account number
3. Confirm account type
4. Match name exactly

### Micro-Deposit Verification Failed
**Symptoms**: Can't verify with deposit amounts

**Solution**:
1. Wait 2-3 business days for deposits
2. Check correct account
3. Note: deposits are cents, not dollars
4. Try again with exact amounts

### Name Mismatch
**Symptoms**: Account rejected, name doesn't match

**Solution**:
- Bank account must be in your name
- Use name exactly as on bank account
- Joint accounts may not work
- Contact support for assistance

## Verification Methods

### Method 1: Micro-Deposits
1. We send two small deposits
2. Check your bank (2-3 days)
3. Enter amounts in Supabank
4. Account verified

### Method 2: Instant Verification
1. Connect via Plaid (if available)
2. Log into your bank
3. Instant verification
4. More secure

### Method 3: Document Upload
1. Upload bank statement
2. Must show:
   - Your name
   - Account number
   - Recent date
3. Verified within 24 hours

## Bank-Specific Issues

### Chase
- Use full account number (not shortened)
- Personal accounts work best

### Bank of America
- Routing varies by state
- Check paper check for correct routing

### Wells Fargo
- Use checking routing, not savings
- Verify account is active

## Still Having Issues?

Contact support with:
- Bank name
- Error message
- Screenshot of issue
- (Don't send full account numbers)
    `.trim(),
  },
  {
    id: "ts-003",
    slug: "delayed-transactions",
    title: "Delayed Transactions",
    description: "What to do when transactions take too long",
    category: "troubleshooting",
    order: 3,
    keywords: ["delayed", "slow", "waiting", "stuck", "pending"],
    content: `
# Delayed Transactions

Transaction taking longer than expected? Here's what to check.

## Deposit Delays

### Check Blockchain Status
1. Get your transaction hash
2. Visit block explorer (Etherscan, etc.)
3. Check confirmations
4. See if pending in mempool

### Common Causes

| Cause | Solution |
|-------|----------|
| Network congestion | Wait for confirmation |
| Low gas fee | Transaction may take hours |
| Wrong network | Contact support |
| Below minimum | Deposit may not process |

### Expected Times
- Ethereum: 2-5 minutes
- Polygon: 3-8 minutes
- Solana: 15-30 seconds
- During congestion: Much longer

## Payout Delays

### Check Supabank Status
1. Go to Transactions
2. Click on payout
3. Check detailed status
4. See estimated arrival

### Common Causes

| Cause | Solution |
|-------|----------|
| First payout | May need review |
| Large amount | Additional verification |
| Bank processing | Wait for bank |
| Weekend/holiday | Banks closed |

### Expected Times
- ACH: 2-3 business days
- Wire: 1-2 business days
- SEPA: 1 business day

## When to Contact Support

### Deposits
- Blockchain confirmed but not showing (>1 hour)
- Status stuck on "Converting" (>30 minutes)
- Amount incorrect

### Payouts
- "Submitted" for >3 business days (ACH)
- "In Transit" for >5 business days
- Returned with no explanation

## Contacting Support

Include:
- Transaction ID
- Expected vs actual timing
- Blockchain hash (deposits)
- Screenshots
    `.trim(),
  },
  {
    id: "ts-004",
    slug: "error-codes-explained",
    title: "Error Codes Explained",
    description: "Understanding Supabank error messages",
    category: "troubleshooting",
    order: 4,
    keywords: ["error", "code", "message", "failed", "issue"],
    content: `
# Error Codes Explained

Understanding error messages helps resolve issues faster.

## Common Error Codes

### Authentication Errors

| Code | Meaning | Solution |
|------|---------|----------|
| AUTH_001 | Session expired | Login again |
| AUTH_002 | Invalid magic link | Request new link |
| AUTH_003 | 2FA required | Enter 2FA code |
| AUTH_004 | Too many attempts | Wait 30 minutes |

### Transaction Errors

| Code | Meaning | Solution |
|------|---------|----------|
| TXN_001 | Insufficient balance | Check balance |
| TXN_002 | Below minimum | Increase amount |
| TXN_003 | Above limit | Reduce amount or upgrade |
| TXN_004 | Invalid address | Verify address format |
| TXN_005 | Unsupported network | Use supported network |

### Bank Account Errors

| Code | Meaning | Solution |
|------|---------|----------|
| BANK_001 | Invalid routing | Check routing number |
| BANK_002 | Invalid account | Check account number |
| BANK_003 | Name mismatch | Use account in your name |
| BANK_004 | Verification failed | Re-verify account |

### Verification Errors

| Code | Meaning | Solution |
|------|---------|----------|
| KYC_001 | Document unclear | Retake photo |
| KYC_002 | Document expired | Use valid ID |
| KYC_003 | Selfie mismatch | Retake selfie |
| KYC_004 | Unsupported region | Check availability |

## Error Message Format

\`\`\`
ERROR: [CODE]
Message: Human-readable description
Details: Additional information
Action: Suggested next step
\`\`\`

## If Error Persists

1. Note the error code
2. Try the suggested solution
3. If still failing, contact support
4. Include: error code, screenshot, steps taken
    `.trim(),
  },
  {
    id: "ts-005",
    slug: "common-issues-faq",
    title: "Common Issues FAQ",
    description: "Quick answers to frequent problems",
    category: "troubleshooting",
    order: 5,
    keywords: ["faq", "common", "questions", "issues", "problems"],
    content: `
# Common Issues FAQ

Quick solutions to the most frequent problems.

## Login Issues

**Q: Magic link not arriving?**
A: Check spam folder, verify email spelling, wait 2 minutes and retry.

**Q: Magic link expired?**
A: Links expire after 15 minutes. Request a new one.

**Q: Can't access my 2FA device?**
A: Use backup codes or contact support for 2FA reset.

## Transaction Issues

**Q: Deposit not showing?**
A: Check blockchain confirmation. Contact support if confirmed >1 hour ago.

**Q: Payout not received?**
A: Check status in app. ACH takes 2-3 business days. Contact support after 5 days.

**Q: Wrong amount credited?**
A: Check exchange rate at time of conversion. Contact support with transaction ID.

## Verification Issues

**Q: KYC keeps failing?**
A: Use good lighting, valid ID, clear photos. Request manual review if issues persist.

**Q: Name doesn't match?**
A: Use name exactly as it appears on your ID. Contact support for legal name change.

## Bank Account Issues

**Q: Bank account rejected?**
A: Double-check all details. Account must be in your name.

**Q: Micro-deposits not received?**
A: Wait 2-3 business days. Check you're monitoring the right account.

## General Issues

**Q: App not loading?**
A: Clear cache, try different browser, check internet connection.

**Q: Can't see recent transactions?**
A: Pull to refresh or click refresh button. Check filters aren't hiding results.

**Q: Need to update email?**
A: Go to Settings > Security > Change Email. Verify both old and new email.

## Still Need Help?

Contact support@supabank.xyz with your specific issue.
    `.trim(),
  },
  {
    id: "ts-006",
    slug: "contacting-support",
    title: "Contacting Support",
    description: "How to get help from our support team",
    category: "troubleshooting",
    popular: true,
    order: 6,
    keywords: ["support", "help", "contact", "assistance", "team"],
    content: `
# Contacting Support

Multiple ways to get help from our team.

## Support Channels

### AI Assistant (Fastest)
- Click chat bubble in app
- Available 24/7
- Instant responses
- Can create tickets

### Email Support
- support@supabank.xyz
- Response: Within 24 hours
- Best for complex issues
- Attach screenshots

### Help Center
- supabank.xyz/support
- Self-service articles
- Step-by-step guides
- Always available

## What to Include

### For Transaction Issues
- Transaction ID
- Amount and currency
- Blockchain hash (if deposit)
- Screenshots

### For Verification Issues
- Account email
- Error messages
- Photos you're trying to use
- Device/browser info

### For Bank Issues
- Bank name
- Error message
- (Don't send account numbers)

## Response Times

| Priority | Response Time |
|----------|---------------|
| Security issues | 1 hour |
| Account access | 4 hours |
| Transaction issues | 24 hours |
| General questions | 24-48 hours |

## Tips for Faster Resolution

**Best practices:**
- Include all relevant details upfront
- Attach screenshots
- Use one thread per issue
- Check spam for replies
- Be specific about the problem

## Escalation

If issue not resolved:
1. Reply to existing ticket
2. Request escalation
3. Include what you've tried
4. Allow 48 hours for escalated review

## Security Issues

For urgent security matters:
- Email: security@supabank.xyz
- Include: Account email, issue type
- Response: Within 1 hour
    `.trim(),
  },
  {
    id: "ts-007",
    slug: "system-status",
    title: "System Status",
    description: "Check Supabank service status",
    category: "troubleshooting",
    order: 7,
    keywords: ["status", "outage", "down", "maintenance", "uptime"],
    content: `
# System Status

Check if Supabank services are operating normally.

## Status Page

Visit: **status.supabank.xyz**

Shows real-time status for:
- Main platform
- API services
- Payment processing
- Blockchain monitoring

## Service Components

### Core Platform
- Web application
- User authentication
- Dashboard functions

### Transaction Processing
- Deposit detection
- Conversion engine
- Payout processing

### Integrations
- Blockchain nodes
- Banking partners
- KYC provider

## Status Indicators

| Status | Meaning |
|--------|---------|
| 🟢 Operational | All systems normal |
| 🟡 Degraded | Slower than usual |
| 🔴 Outage | Service unavailable |
| 🔵 Maintenance | Planned downtime |

## Subscribing to Updates

1. Visit status.supabank.xyz
2. Click "Subscribe"
3. Enter email
4. Receive notifications for:
   - Service disruptions
   - Maintenance windows
   - Resolution updates

## During Outages

### What to Do
- Check status page first
- Don't retry transactions repeatedly
- Wait for resolution
- Follow @supabank on Twitter for updates

### What NOT to Do
Create multiple support tickets
Retry failed transactions continuously
Assume funds are lost

## Scheduled Maintenance

- Announced 48 hours in advance
- Usually during low-traffic hours
- Typically brief (< 30 minutes)
- Updates posted on status page

## Historical Uptime

- 99.9% uptime target
- Incident history on status page
- Post-incident reports published
    `.trim(),
  },
  {
    id: "ts-008",
    slug: "scheduled-maintenance",
    title: "Scheduled Maintenance",
    description: "Understanding planned maintenance windows",
    category: "troubleshooting",
    order: 8,
    keywords: ["maintenance", "scheduled", "downtime", "planned", "window"],
    content: `
# Scheduled Maintenance

How we handle planned maintenance to minimize disruption.

## Maintenance Windows

### Regular Maintenance
- When: Sundays 2-4 AM UTC
- Duration: Usually < 30 minutes
- Impact: Minimal to none

### Major Updates
- Announced 48+ hours ahead
- Scheduled during low traffic
- May have brief service interruption

## What Happens During Maintenance

### Typically Affected
- New transactions may pause
- UI may show maintenance page
- API may return maintenance status

### Usually Unaffected
- Pending transactions continue
- Blockchain monitoring
- Bank transfers in progress

## How We Notify You

### 48+ Hours Before
- Email notification (if subscribed)
- Status page announcement
- In-app banner

### During Maintenance
- Status page updated
- Maintenance screen shown
- Estimated completion time

### After Completion
- Status page updated
- Normal service restored
- Summary posted (if major)

## What You Should Do

### Before Maintenance
Complete urgent transactions
Don't start large operations
Note the expected duration

### During Maintenance
Check status page for updates
Wait patiently
Don't retry operations repeatedly

### After Maintenance
Refresh your browser
Verify pending transactions
Report any issues

## Emergency Maintenance

Occasionally needed for:
- Security patches
- Critical bug fixes
- Infrastructure issues

These have shorter notice but follow similar communication patterns.

## Questions?

Contact support@supabank.xyz for maintenance-related questions.
    `.trim(),
  },
];
